import { IconRectangle } from '@tabler/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import { useAppSelector } from '../../../store';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  Delete,
  Language,
  Notifications,
  Palette,
  Payments,
  Security,
  Telegram,
  Login,
  Tune
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router';
import DeleteDialog from '../../../components/material-ui/dialog/DeleteDialog';
import { API_CASINO } from '../../../api/casino';
import { dispatchCusEvent } from '../../../utils';
import { ENUM_CUS_EVENT, TEST_MODE } from '../../../enums/enums';
import InfoDialog from '../../../components/shared/InfoDialog';

export default function CasinoMenu({ casinoId, openItemMenuInSetting, isDelete }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const borderRadius = useAppSelector((state) => state.customizer.borderRadius);
  const lang = useAppSelector((state) => state.site.lang);
  const casinos = useAppSelector((state) => state.user.info.casino);
  const isDeleteCasino = useAppSelector((state) => state.user.casinoActions.delete);

  const br = `${borderRadius}px`;
  const pathname = location.pathname;
  const pageSearch = location.search;
  const defaultOpen = new URLSearchParams(pageSearch).get('openMenu');
  const casinoName = casinos.find((el) => el.id == casinoId)?.casinoName;

  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openChild, setOpenChild] = useState([+defaultOpen]);
  const [selectId, setSelectId] = useState();

  const onDeleteConfirm = () => {
    if (TEST_MODE) {
      setSelectId(null);
      navigate('/settings/casino');
      return;
    }

    API_CASINO.delete({ casinoId })
      .then((e) => {
        const result = e.data.result;
        if (result.ok) {
          dispatchCusEvent(ENUM_CUS_EVENT.getCasinos);
          setSelectId(null);
          navigate('/settings/casino');
          return;
        }

        setOpenGroupModal(result.groupList);
      })
      .catch(console.error);
  };

  const searchSelectLink = (items, link) => {
    for (const item of items) {
      // Проверяем текущий элемент
      if (item.link === link) return item;

      // Рекурсивно ищем в children
      if (item.children) {
        const foundInChildren = searchSelectLink(item.children, link);
        if (foundInChildren) return foundInChildren;
      }
    }
    return null;
  };

  const actions = [
    {
      id: 1,
      title: 'Common settings',
      icon: Tune,
      children: [
        { id: 1.1, title: 'Name and group', link: `/casino/common/names/${casinoId}` },

        { id: 1.3, title: 'Domains and aliases', link: `/casino/common/domains/${casinoId}` },
        {
          id: 1.4,
          title: 'Registration and authorization',
          link: `/casino/common/auth/${casinoId}`
        },
        { id: 1.5, title: 'Edit "Agreement"', link: `/casino/common/agreement/${casinoId}` }
      ]
    },
    {
      id: 2,
      title: 'Payments and payouts',
      icon: Payments,
      children: [
        {
          id: 2.1,
          title: 'Game balance currencies',
          link: `/casino/payment/currencies/${casinoId}`
        },
        {
          id: 2.2,
          title: 'Payment providers cascade',
          link: `/casino/payment/cascade/${casinoId}`
        },
        {
          id: 2.3,
          title: 'Deposit limits',
          link: `/casino/payment/deposits/${casinoId}`
        },
        {
          id: 2.4,
          title: 'Withdrawal limits',
          link: `/casino/payment/withdrawal/${casinoId}`
        },
        {
          id: 2.5,
          title: 'Crypto gateway settings',
          link: `/casino/payment/gateway/${casinoId}`
        },
        {
          id: 2.6,
          title: 'Payment providers settings',
          link: `/casino/payment/setting/${casinoId}`
        },
        {
          id: 2.7,
          title: 'Other payment settings',
          link: `/casino/payment/other/${casinoId}`
        }
      ]
    },
    { id: 3, title: 'Telegram casino', icon: Telegram, link: `/casino/tg-casino/${casinoId}` },
    {
      id: 4,
      title: 'Mailings',
      icon: Notifications,
      children: [
        {
          id: 4.1,
          title: 'Telegram notifications for players',
          link: `/casino/mailing/players/${casinoId}`
        },
        {
          id: 4.2,
          title: 'Telegram notifications for managers',
          link: `/casino/mailing/managers/${casinoId}`
        }
      ]
    },
    {
      id: 5,
      title: 'Website design',
      icon: Palette,
      children: [
        { id: 5.1, title: 'Logo', link: `/casino/design/logo/${casinoId}` },
        { id: 5.2, title: 'Colors', link: `/casino/design/color/${casinoId}` },
        { id: 5.3, title: 'Banners', link: `/casino/design/banners/${casinoId}/${lang}` },
        {
          id: 5.4,
          title: 'Inserting HTML code into the <title> and <head>',
          link: `/casino/design/head/${casinoId}`
        },
        { id: 5.5, title: 'HTML before </body>', link: `/casino/design/body/${casinoId}` },
        { id: 5.6, title: 'tawk.io chat', link: `/casino/design/tawk/${casinoId}` },
        { id: 5.7, title: 'Online players count', link: `/casino/design/players/${casinoId}` }
      ]
    },
    { id: 6, title: 'Languages and links', icon: Language, link: `/casino/lang-links/${casinoId}` },
    { id: 7, title: 'Captcha settings', icon: Security, link: `/casino/captcha/${casinoId}` },
    {
      id: 8,
      title: 'Website Login / mailings',
      icon: Login,
      link: `/casino/authorization/${casinoId}`
    }
  ];

  useMemo(() => {
    const selectItems = searchSelectLink(actions, pathname);

    if (!selectItems) return;
    const selectId = selectItems.id;
    const parentId = +String(selectId).split('.')?.[0];

    if (parentId) {
      setOpenChild((prev) => [...prev, parentId]);
      openItemMenuInSetting.current = parentId;
    }
    setSelectId(selectId);
  }, [pathname]);

  return (
    <>
      {actions.map((item) => {
        const itemId = item.id;

        const openChildrenOnClick = () => {
          setOpenChild((prev) =>
            prev.includes(itemId) ? prev.filter((el) => el != itemId) : [...prev, itemId]
          );
        };

        return (
          <ShowContainer condition={casinoId} key={itemId}>
            <ListItemButton
              selected={item.id == selectId}
              sx={{ mb: 0.5, mx: 0, borderRadius: br }}
              onClick={item.children ? openChildrenOnClick : item.onClick}
              LinkComponent={RouterLink}
              to={item.link}>
              <ShowContainer condition={item.icon}>
                <ListItemIcon sx={{ minWidth: '30px', color: item.chipColor }}>
                  <item.icon stroke="1.5" size={19} />
                </ListItemIcon>
              </ShowContainer>
              <ListItemText primary={t(item.title)} />
            </ListItemButton>

            <ShowContainer condition={item.children}>
              <Collapse in={openChild.includes(item.id)}>
                <List component="div" disablePadding>
                  {item.children?.map((child) => (
                    <ListItemButton
                      selected={child.id == selectId}
                      sx={{ pl: 4 }}
                      key={child.id}
                      LinkComponent={RouterLink}
                      to={child.link}>
                      <ListItemText secondary={t(child.title)} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </ShowContainer>
          </ShowContainer>
        );
      })}

      <ShowContainer condition={casinoId && isDelete && isDeleteCasino}>
        <ListItemButton
          sx={{ mb: 0.5, mx: 0, borderRadius: br }}
          onClick={() => setOpenDelete(true)}>
          <ListItemIcon sx={{ minWidth: '30px' }}>
            <Delete stroke="1.5" size={19} />
          </ListItemIcon>
          <ListItemText primary={t('Delete casino')} />
        </ListItemButton>
      </ShowContainer>

      <DeleteDialog
        title={`${t('Delete the casino')} "${casinoName}"`}
        content={`${t('Are you sure you want to delete the casino?')} "${casinoName} (id:${casinoId})" ?`}
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={onDeleteConfirm}
      />

      <InfoDialog
        open={openGroupModal}
        onClose={() => setOpenGroupModal(false)}
        title={`${t('Impossible to delete casino')} "${casinoName}"`}>
        <Stack direction={'column'} gap={4}>
          <Typography>
            {t(
              'Deletion is prohibited because the casino currently contains player accounts. Please first delete or move these accounts from the groups listed below.'
            )}
          </Typography>

          <List>
            <Typography variant="h6" mb={1}>
              {t('Group list')}
            </Typography>
            {Object.keys(openGroupModal || {}).map((casinoId) => (
              <ListItemText key={casinoId}>{openGroupModal[casinoId]}</ListItemText>
            ))}
          </List>
        </Stack>
      </InfoDialog>
    </>
  );
}
